import React from 'react';
import { CheckCircle, XCircle, Loader2, AlertCircle, GitBranch, Archive } from 'lucide-react';
import type { AnalyzedRepository } from '../types/history';

export const ExecutionBadge: React.FC<{ status: AnalyzedRepository['executionStatus'] }> = ({
  status,
}) => {
  const config = {
    analyzing: {
      icon: Loader2,
      color: 'text-blue-600 dark:text-blue-400',
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      text: 'Analyzing',
    },
    completed: {
      icon: CheckCircle,
      color: 'text-green-600 dark:text-green-400',
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      text: 'Completed',
    },
    failed: {
      icon: XCircle,
      color: 'text-red-600 dark:text-red-400',
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      text: 'Failed',
    },
  }[status];

  if (!config) return null;
  const Icon = config.icon;

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.color} ${config.border}`}
    >
      <Icon className={`w-3.5 h-3.5 ${status === 'analyzing' ? 'animate-spin' : ''}`} />
      {config.text}
    </div>
  );
};

export const HealthBadge: React.FC<{ health: AnalyzedRepository['health'] }> = ({ health }) => {
  if (!health) return null;

  const config = {
    healthy: {
      icon: CheckCircle,
      color: 'text-green-600 dark:text-green-400',
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      text: 'Healthy',
    },
    issues: {
      icon: AlertCircle,
      color: 'text-yellow-600 dark:text-yellow-400',
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-800',
      text: 'Issues Found',
    },
  }[health];

  if (!config) return null;
  const Icon = config.icon;

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.color} ${config.border}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {config.text}
    </div>
  );
};

export const ProviderBadge: React.FC<{ provider: string }> = ({ provider }) => {
  const providerConfig = {
    github: {
      color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      text: 'GitHub',
      icon: GitBranch,
    },
    gitlab: {
      color: 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300',
      text: 'GitLab',
      icon: GitBranch,
    },
    bitbucket: {
      color: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
      text: 'Bitbucket',
      icon: GitBranch,
    },
    upload: {
      color: 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
      text: 'Upload',
      icon: Archive,
    },
  };

  const config = providerConfig[provider as keyof typeof providerConfig] || providerConfig.github;
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}
    >
      <Icon className="w-3 h-3" />
      {config.text}
    </span>
  );
};
